import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormDialogMode,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Button } from '@material-ui/core'
import { Block, Delete, Edit } from '@material-ui/icons'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import {
  CancelMeterModel,
  CUPSModel,
  MeterModel,
  UseModel
} from '../../../../api/Argentia/model/contracts'
import { EnumTipoCaudal } from '../../../../api/oData/enums/enumTipoCaudal'
import { EnumTipoCorrector } from '../../../../api/oData/enums/enumTipoCorrector'
import { EnumTipoMedidor } from '../../../../api/oData/enums/enumTipoMedidor'
import { EmptyContent } from '../../../../components/shared/emptyCardContent/emptyContent'
import SimpleDialog from '../../../../components/shared/simpleDialog/simpleDialog'
import { enumToSelectOptionArray } from '../../../../helpers'
import { startDateIsBeforeEndDate } from '../../../../helpers/formValidationsHelper'
import { FormDialogDefinitionProps, useFormDialog } from '../../../../hooks'
import { NonAuthRoutes } from '../../../../model'
import { getCustomerDomain } from '../../../../redux'
import { UsesTable } from '../../../uses/components/usesTable/usesTable'
import { mapUseRequestPatch } from '../../../uses/uses.mapper'
import { UseModelPatch } from '../../../uses/uses.model'
import {
  createUseFromMeter,
  deleteUseFromMeter,
  updateUseFromMeter
} from '../../../uses/uses.service'
import { mapCancelMeterRequest, mapMetersFromCups, mapUsesFromCups } from '../../meters.mapper'
import { MeterModelPatch } from '../../meters.model'
import { cancelMeter, deleteMeter, updateMeter } from '../../meters.service'

interface MetersTableProps {
  cupsData: CUPSModel[] | undefined
  onMeterUpdate: () => void
}

export const MetersTable: React.FC<MetersTableProps> = ({ cupsData, onMeterUpdate }) => {
  const [showUsesDialog, setShowUsesDialog] = useState<boolean>(false)
  const [dateIsInvalid, setDateIsInvalid] = useState<boolean>(false)
  const [usesData, setUsesData] = useState<UseModel[]>([])
  const [selectedMeterData, setSelectedMeterData] = useState<MeterModel>({} as MeterModel)

  const [t] = useTranslation(['globalerisLibrary', 'meters', 'enums', 'uses'])
  const { info, error } = useNotify()
  const history = useHistory()
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()

  const metersData = cupsData && mapMetersFromCups(cupsData[0])
  const commodity = cupsData && cupsData[0].commodity

  // #region Handlers Starts

  const handleInsertMeterAction = async (row): Promise<boolean> => {
    history.push(NonAuthRoutes.MeterAdd, {
      cupsData: cupsData
    })
    return await true
  }

  const handleEditMeterAction = (rowData: MeterModel) => {
    const oldData = { ...rowData }
    openForm({
      definition: meterEditFormDefinition,
      title: t('meters:components.metersTable.forms.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: rowData,
      onClose: formData => {
        handleEditRowFormClose(formData, oldData as MeterModel)
      }
    })
  }

  const handleEditRowFormClose = async (formData: MeterModel, oldData: MeterModel) => {
    if (formData) {
      // const patchRequest: MeterModelPatch = mapDirtyFormInputs(oldData, formData) // this line was modified beacause in Argentia (OData.Client) when ignoreNulls = false, sending only one property will put other as null by default.
      const patchRequest: MeterModelPatch = formData

      await updateMeter(patchRequest).then(response => {
        if (response.success) {
          info(t('meters:components.metersTable.forms.edit.notifications.save.success'))
          onMeterUpdate()
        } else {
          error(t('meters:components.metersTable.forms.edit.notifications.save.error'))
        }
      })
    }
    closeForm()
  }

  const handleDeleteMeterAction = async (formData: MeterModel) => {
    if (formData) {
      await deleteMeter(formData.id!).then(response => {
        if (response.success) {
          info(t('meters:components.metersTable.notifications.delete.success'))
          onMeterUpdate()
        } else {
          error(t('meters:components.metersTable.notifications.delete.error'))
        }
      })
    }
    closeForm()
  }

  const handleCancelMeterAction = async (row: MeterModel) => {
    openForm({
      definition: cancelMeterFormDefinition,
      title: t('meters:components.metersTable.forms.cancelMeter.title'),
      subtitle: t('meters:components.metersTable.forms.cancelMeter.subtitle'),
      mode: FormDialogMode.ADD,
      inputData: row,
      isWaiting: false,
      onClose: handleCancelMeterFormClose
    })
  }

  const handleCancelMeterFormClose = async (formData: CancelMeterModel) => {
    if (formData) {
      const meterToCancel: CancelMeterModel = mapCancelMeterRequest(formData)
      await cancelMeter(meterToCancel).then(async response => {
        if (response.success) {
          info(t('meters:components.metersTable.notifications.cancelMeter.success'))
          onMeterUpdate()
        } else {
          error(t('meters:components.metersTable.notifications.cancelMeter.error'))
        }
      })
    }
    closeForm()
  }

  const onViewUsesClickHandler = (row: MeterModel) => {
    if (cupsData) {
      setSelectedMeterData(row)
      setUsesData(mapUsesFromCups(cupsData[0], row?.id!))
      setShowUsesDialog(true)
    }
  }

  const onViewUsesCloseHandler = () => {
    setShowUsesDialog(false)
  }

  const handleAddUse = async (row: UseModel) => {
    setShowUsesDialog(false)
    await createUseFromMeter(row, selectedMeterData.id!).then(async response => {
      if (response.success) {
        info(t('uses:components.usesDataTable.notifications.add.success'))
        onMeterUpdate()
      } else {
        error(response.error?.message ?? t('uses:components.usesDataTable.notifications.add.error'))
      }
    })
  }

  const handleEditUse = async (newData: UseModel, oldData: UseModel) => {
    setShowUsesDialog(false)
    // const patchRequest: UseModelPatch = mapUseRequestPatch(mapDirtyFormInputs(oldData, newData))
    const patchRequest: UseModelPatch = mapUseRequestPatch(newData) // this line was modified beacause in Argentia (OData.Client) when ignoreNulls = false, sending only one property will put other as null by default.
    await updateUseFromMeter(patchRequest, selectedMeterData.id!).then(async response => {
      if (response.success) {
        info(t('uses:components.usesDataTable.notifications.edit.success'))
        onMeterUpdate()
      } else {
        error(
          response.error?.message ?? t('uses:components.usesDataTable.notifications.edit.error')
        )
      }
    })
  }

  const handleDeleteUse = async (row: UseModel) => {
    setShowUsesDialog(false)
    await deleteUseFromMeter(row.id!, selectedMeterData.id!).then(async response => {
      if (response.success) {
        info(t('uses:components.usesDataTable.notifications.delete.success'))
        onMeterUpdate()
      } else {
        error(
          response.error?.message ?? t('uses:components.usesDataTable.notifications.delete.error')
        )
      }
    })
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  // const validateEndDate = (value: string, formState: MeterModel) => {
  //   const endDateBeforeStartDate = moment(value).isBefore(moment(formState.startDate))
  //   if (endDateBeforeStartDate) {
  //     setDateIsInvalid(true)
  //   } else {
  //     setDateIsInvalid(false)
  //   }
  // }
  // #endregion Functions Ends

  // #region Form Definition Starts
  const meterEditFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT_METER',
        sections: [
          {
            inputs: [
              {
                name: 'um',
                label: t('meters:components.metersTable.forms.edit.inputs.um'),
                type: DataType.TEXT,
                required: true
              },
              {
                name: 'meterType',
                label: t('meters:components.metersTable.forms.edit.inputs.meterType'),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(EnumTipoMedidor, 'enums:EnumTipoMedidor'),
                clearable: true
              },
              {
                name: 'description',
                label: t('meters:components.metersTable.forms.edit.inputs.description'),
                type: DataType.TEXT,
                required: true
              },
              {
                name: 'code',
                label: t('meters:components.metersTable.forms.edit.inputs.code'),
                type: DataType.TEXT,
                required: true
              },
              {
                name: 'correctorType',
                label: t('meters:components.metersTable.forms.edit.inputs.correctorType'),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(
                  EnumTipoCorrector,
                  'enums:EnumTipoCorrector'
                ),
                clearable: true
              },
              {
                name: 'flowType',
                label: t('meters:components.metersTable.forms.edit.inputs.flowType'),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(EnumTipoCaudal, 'enums:EnumTipoCaudal'),
                clearable: true
              },
              {
                name: 'weight',
                label: t('meters:components.metersTable.forms.edit.inputs.weight'),
                type: DataType.TEXT
              },
              {
                name: 'startDate',
                label: t('meters:components.metersTable.forms.edit.inputs.startDate'),
                type: DataType.DATE,
                required: true,
                errorState: dateIsInvalid,
                errorText: t('meters:components.metersTable.forms.edit.validations.startDate'),
                onChange: (value, formState) => {
                  const formData = formState as MeterModel
                  setDateIsInvalid(!startDateIsBeforeEndDate(formData.startDate, formData.endDate))
                }
              },
              {
                name: 'endDate',
                label: t('meters:components.metersTable.forms.edit.inputs.endDate'),
                type: DataType.DATE,
                clearable: true,
                errorState: dateIsInvalid,
                onChange: (value, formState) => {
                  const formData = formState as MeterModel
                  setDateIsInvalid(!startDateIsBeforeEndDate(formData.startDate, formData.endDate))
                }
              },
              {
                name: 'rented',
                label: t('meters:components.metersTable.forms.edit.inputs.rented'),
                type: DataType.BOOLEAN
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateIsInvalid])

  const cancelMeterFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'CANCEL_METER',
        sections: [
          {
            inputs: [
              {
                name: 'id',
                type: DataType.NUMBER,
                display: false
              },
              {
                name: 'startDate',
                label: t('meters:components.metersTable.forms.edit.inputs.startDate'),
                type: DataType.DATE,
                readOnly: true
              },
              {
                name: 'endDate',
                label: t('meters:components.metersTable.forms.edit.inputs.endDate'),
                type: DataType.DATE,
                errorState: dateIsInvalid,
                errorText: t('meters:components.metersTable.forms.cancelMeter.validations.endDate'),
                onChange: (value, formState) => {
                  const formData = formState as MeterModel
                  setDateIsInvalid(!startDateIsBeforeEndDate(formData.startDate, formData.endDate))
                },
                required: true
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateIsInvalid])
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const options = React.useMemo((): DataTableOptions => {
    return {
      i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
      search: false,
      elevation: 0,
      filterType: FilterType.NONE,
      download: false,
      canEdit: false,
      canDelete: false,
      canInsert: true,
      customInsertEditModal: true,

      pagination: true,
      rowsPerPage: 10,
      rowsPerPageOptions: undefined,

      analytics: {
        customer: getCustomerDomain(),
        view: t('meters:components.metersTable.dataTable.analytics')
      },
      onRowInsert: handleInsertMeterAction,
      rowCustomActions: [
        {
          icon: <Edit fontSize="small" />,
          tooltip: t('meters:components.metersTable.dataTable.tooltips.edit'),
          onClick: handleEditMeterAction,
          userPermission: UserPermission.EDIT
        },
        {
          icon: <Block fontSize="small" />,
          tooltip: t('meters:components.metersTable.dataTable.tooltips.cancelMeter'),
          onClick: handleCancelMeterAction,
          userPermission: UserPermission.EDIT
        },
        {
          icon: <Delete fontSize="small" />,
          tooltip: t('meters:components.metersTable.dataTable.tooltips.delete'),
          onClick: handleDeleteMeterAction,
          userPermission: UserPermission.EDIT
        },
        {
          icon: <NavigateNextIcon fontSize="small" />,
          tooltip: t('meters:components.metersTable.dataTable.tooltips.viewUses'),
          onClick: onViewUsesClickHandler
          // userPermission: UserPermission.EDIT
        }
      ]
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [cupsData])

  const columns = React.useMemo((): DataTableColumn[] => {
    return [
      {
        name: 'id',
        display: false
      },
      {
        name: 'um',
        label: t('meters:components.metersTable.dataTable.inputs.um'),
        type: DataType.TEXT
      },
      {
        name: 'meterType',
        label: t('meters:components.metersTable.dataTable.inputs.meterType'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumTipoMedidor, 'enums:EnumTipoMedidor')
      },
      {
        name: 'description',
        label: t('meters:components.metersTable.dataTable.inputs.description'),
        type: DataType.TEXT
      },
      {
        name: 'code',
        label: t('meters:components.metersTable.dataTable.inputs.code'),
        type: DataType.TEXT
      },
      {
        name: 'correctorType',
        label: t('meters:components.metersTable.dataTable.inputs.correctorType'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumTipoCorrector, 'enums:EnumTipoCorrector')
      },
      {
        name: 'flowType',
        label: t('meters:components.metersTable.dataTable.inputs.flowType'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumTipoCaudal, 'enums:EnumTipoCaudal')
      },
      {
        name: 'weight',
        label: t('meters:components.metersTable.dataTable.inputs.weight'),
        type: DataType.TEXT
      },
      {
        name: 'startDate',
        label: t('meters:components.metersTable.dataTable.inputs.startDate'),
        type: DataType.DATE
      },
      {
        name: 'endDate',
        label: t('meters:components.metersTable.dataTable.inputs.endDate'),
        type: DataType.DATE
      },
      {
        name: 'rented',
        label: t('meters:components.metersTable.dataTable.inputs.rented'),
        type: DataType.BOOLEAN
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // #endregion DataTable Config Ends

  // #region Hooks Starts
  useEffect(() => {
    updateFormDefinition(cancelMeterFormDefinition)
    updateFormDefinition(meterEditFormDefinition)
  }, [meterEditFormDefinition, cancelMeterFormDefinition])
  // #endregion Hooks Ends

  return (
    <>
      {metersData && metersData.length > 0 ? (
        <DataTable
          data={metersData}
          columns={columns}
          options={options}
          // userPermissions={getUserPermissions(Pages.Client)}
        />
      ) : (
        <EmptyContent caption={t('meters:components.emptyCard.caption')}>
          <Button
            size="small"
            color="primary"
            variant="contained"
            onClick={handleInsertMeterAction}
          >
            {t('meters:components.emptyCard.buttonLabel')}
          </Button>
        </EmptyContent>
      )}
      <SimpleDialog
        open={showUsesDialog}
        handleClose={onViewUsesCloseHandler}
        title={t('meters:components.metersTable.usesDialog.title')}
      >
        <UsesTable
          listUsesData={usesData}
          commodity={commodity}
          handleAddUse={handleAddUse}
          handleEditUse={handleEditUse}
          handleDeleteUse={handleDeleteUse}
        />
      </SimpleDialog>

      <CustomFormDialog {...formProps} />
    </>
  )
}
